/**
 * Saved-layout panel-id migrations applied to a persisted dockview
 * ``dockview_state`` at restore time, BEFORE shape + role-accessibility
 * checks and BEFORE dockview's ``fromJSON`` apply (REQ-UI-054 / REQ-UI-061
 * §4.7.1).
 *
 * # Why this is its own (dockview-free) module
 *
 * The migration is pure data-rewriting over the :type:`SerializedDockview`
 * *shape* — it needs the type but not the dockview-react runtime. Importing
 * the workspace component (:file:`./multi-pane-workspace.tsx`) into a vitest
 * test would transitively trigger dockview-react's CSS-in-JS injection at
 * module-load, which is incompatible with jsdom (the same hazard
 * :file:`./presets.ts` documents and avoids). Keeping the migration here —
 * with a TYPE-ONLY ``SerializedDockview`` import — lets it be unit-tested in
 * vitest while the workspace consumes it on the real layout-load path.
 *
 * Cross-references:
 *   - REQ-UI-054 (schema-mismatch / forward-migration on restore).
 *   - REQ-UI-061 §4.7.1 (ADR-032 events→detections; PR6).
 *   - ADR-024 §6 (two-tier ownership: Meridian semantic layer + opaque
 *     dockview_state).
 *   - multi-pane/presets.ts (the type-only-import discipline mirrored here).
 */
import type { SerializedDockview } from "dockview-react";
import type { PanelId } from "@/multi-pane/registry-types";
import { PANEL_TITLES } from "@/multi-pane/registry-types";

/**
 * Retired panel id → successor. Each entry rewrites a persisted panel id on
 * load.
 *
 * # Events → Detections (ADR-032 cascade, PR6; REQ-UI-061 §4.7.1)
 *
 * The data-plane Events surface was removed in PR6 (Detection + Alert
 * supersede it). A user who had the ``events`` panel docked in their saved
 * layout must NOT get a blank/broken panel on next sign-in — dockview would
 * fail to resolve the ``events`` ``contentComponent`` (no longer in the panel
 * registry) and that group would render empty. We therefore rewrite a
 * persisted ``events`` panel id → ``detections`` on load, mirroring the
 * built-in :data:`DEFAULT_LAYOUT` (which carries ``detections``, not the
 * retired ``events``) so a restored custom layout lands on the successor
 * surface in the same slot.
 *
 * Mapping to a successor (rather than stripping) is clean here because
 * ``detections`` carries the same role gating as ``events`` did and the
 * Detection panel is the intended replacement — the user keeps a populated
 * panel where Events used to be.
 *
 * # System Health → System Status (Flow L; ADR-035 / S6)
 *
 * The ``system-health`` panel was superseded by ``system-status`` (Flow L; same
 * support_engineer + admin gating per REQ-STATUS-029). A saved layout that
 * docked ``system-health`` is rewritten to ``system-status`` on load so the
 * support engineer lands on the successor surface in the same slot — identical
 * rationale to the events → detections rewrite above.
 */
/**
 * The operator-facing warning raised when a saved ``dockview_state`` blob
 * cannot be deserialized and the workspace falls back to the default layout.
 *
 * **The wording is prescribed verbatim by REQ-UI-054's Statement** — it is a
 * requirement, not editorial copy, so it lives here as a single source of
 * truth the workspace and its tests both read rather than being inlined at
 * the two recovery sites.
 *
 * Until #726 this requirement was satisfied only by a ``console.warn``: the
 * layout silently reverted and the operator was told nothing, which
 * CLAUDE.md §8.1 (operator visibility) does not permit. The Flow-F toast
 * host now carries it.
 */
export const LAYOUT_RESTORE_FAILED_MESSAGE =
  "Your saved layout couldn't be restored after a UI update. Default layout " +
  "applied. You can re-save your preferred layout from the gear menu.";

export const PERSISTED_PANEL_ID_MIGRATIONS: Readonly<Record<string, PanelId>> = {
  events: "detections",
  "system-health": "system-status",
};

/**
 * Rewrite retired panel ids in a parsed :type:`SerializedDockview` per
 * :data:`PERSISTED_PANEL_ID_MIGRATIONS`. Returns a NEW object when a
 * migration applies (the input is treated as immutable); returns the input
 * unchanged by reference when there is nothing to migrate.
 *
 * Touches every place a panel id is referenced in dockview's serialized
 * shape:
 *
 *   - ``panels`` — the record key, plus each descriptor's ``id`` and
 *     ``contentComponent`` (the preset builder sets ``contentComponent``
 *     equal to the id; the panel-component registry is keyed by id).
 *   - grid leaves — each leaf's ``data.views[]`` + ``data.activeView``.
 *
 * The leaf ``id`` (the GROUP id, e.g. ``group-ops-events``) and the
 * top-level ``activeGroup`` (also a group id) are deliberately left alone —
 * they are group identifiers, not panel ids, and dockview resolves panels by
 * the ``views``/``panels`` references, not the group-id string.
 *
 * If a migrated id would COLLIDE with a panel id already present (e.g. the
 * user somehow had both ``events`` and ``detections`` docked), the existing
 * target wins and the retired panel is dropped from ``panels`` — duplicate
 * panel ids are invalid in dockview, so we cannot keep both.
 */
export function migrateLayoutPanels(state: SerializedDockview): SerializedDockview {
  const remap = (id: string): string =>
    Object.prototype.hasOwnProperty.call(PERSISTED_PANEL_ID_MIGRATIONS, id)
      ? PERSISTED_PANEL_ID_MIGRATIONS[id]
      : id;

  // Fast path: nothing to migrate. Avoids cloning a layout that has no
  // retired ids (the overwhelmingly common case post-migration-window).
  const json = JSON.stringify(state);
  if (!Object.keys(PERSISTED_PANEL_ID_MIGRATIONS).some((id) => json.includes(`"${id}"`))) {
    return state;
  }

  // Deep clone so the persisted document + any other reader is untouched.
  const next = JSON.parse(json) as SerializedDockview;

  // 1) Rewrite the panels record (key + descriptor id + contentComponent).
  // ``id`` and ``contentComponent`` are typed string fields on dockview's
  // panel descriptor, so they remap directly; the rest of the descriptor is
  // spread through unchanged.
  const panels = next.panels ?? {};
  const rebuilt: typeof panels = {};
  for (const [key, descriptor] of Object.entries(panels)) {
    const newKey = remap(key);
    const migratedDescriptor: (typeof panels)[string] = {
      ...descriptor,
      id: remap(descriptor.id),
      contentComponent:
        descriptor.contentComponent !== undefined
          ? remap(descriptor.contentComponent)
          : descriptor.contentComponent,
    };
    // Collision guard: if the target key already exists (a real panel was
    // docked under it), keep the existing one — duplicate ids are invalid.
    if (rebuilt[newKey] === undefined) {
      rebuilt[newKey] = migratedDescriptor;
    }
  }
  next.panels = rebuilt;

  // 2) Rewrite the grid-leaf view references in place.
  const visitNode = (node: unknown): void => {
    if (node === null || typeof node !== "object") {
      return;
    }
    const rec = node as Record<string, unknown>;
    if (rec.type === "leaf" && rec.data !== null && typeof rec.data === "object") {
      const data = rec.data as Record<string, unknown>;
      if (Array.isArray(data.views)) {
        // Map each view id; de-dupe in case a collision collapsed two views
        // onto the same target (a view list must not contain duplicates).
        const mapped = (data.views as unknown[]).map((v) => (typeof v === "string" ? remap(v) : v));
        data.views = mapped.filter((v, i) => mapped.indexOf(v) === i);
      }
      if (typeof data.activeView === "string") {
        data.activeView = remap(data.activeView);
      }
    } else if (rec.type === "branch" && Array.isArray(rec.data)) {
      for (const child of rec.data as unknown[]) {
        visitNode(child);
      }
    }
  };
  if (next.grid?.root !== undefined) {
    visitNode(next.grid.root);
  }

  return next;
}

/**
 * Insert the ``query`` panel into a saved layout that predates it, as a tab
 * in the group containing ``map``, immediately after it (ADR-038 D15 /
 * REQ-UI-112).
 *
 * # Why a layout migration is needed at all
 *
 * Adding ``query`` to :data:`DEFAULT_LAYOUT` reaches only users with no
 * saved ``dockview_state``. Everyone who has ever saved a layout — which
 * is everyone with a Profile — would restore into a workspace with no
 * Query tab and no indication one exists, reachable only by knowing to
 * look in the Add-panel menu. The specified default position has to be
 * applied to existing layouts too, or it is not the default for anyone
 * who has used the product.
 *
 * # What it does and deliberately does not do
 *
 * - **Idempotent.** A layout already carrying ``query`` (because it was
 *   saved after this shipped, or migrated on a previous restore) is
 *   returned unchanged by reference.
 * - **Map-anchored.** The tab lands in the group hosting ``map``, one
 *   position to its right — the position REQ-UI-111 specifies.
 * - **No Map, no insertion.** A custom layout that dropped the Map has no
 *   anchor the requirement names, and guessing a group would move a
 *   surface into a pane the operator deliberately shaped. Query stays
 *   reachable from the Add-panel menu; silently doing nothing is the
 *   conservative half of CLAUDE.md §8.1, not an oversight.
 *
 * Role gating is *not* applied here — ``query`` is readable by every
 * authenticated role (REQ-AUTH-075), and the restore path runs
 * :func:`filterLayoutByRole` downstream regardless.
 *
 * Returns a NEW object when the insertion applies; the input unchanged by
 * reference otherwise.
 */
export function insertQueryPanel(state: SerializedDockview): SerializedDockview {
  const target: PanelId = "query";
  const anchor: PanelId = "map";

  if (state.panels?.[target] !== undefined) {
    return state;
  }

  // Deep clone before mutating — the persisted document and any other
  // reader of this layout must be untouched (same discipline as above).
  const next = JSON.parse(JSON.stringify(state)) as SerializedDockview;

  let inserted = false;
  const visitNode = (node: unknown): void => {
    if (inserted || node === null || typeof node !== "object") {
      return;
    }
    const rec = node as Record<string, unknown>;
    if (rec.type === "leaf" && rec.data !== null && typeof rec.data === "object") {
      const data = rec.data as Record<string, unknown>;
      if (!Array.isArray(data.views)) return;
      const views = data.views as unknown[];
      const anchorIndex = views.indexOf(anchor);
      if (anchorIndex === -1) return;
      views.splice(anchorIndex + 1, 0, target);
      // ``activeView`` is deliberately left alone: the operator's active
      // tab must not change under them just because a surface was added.
      inserted = true;
    } else if (rec.type === "branch" && Array.isArray(rec.data)) {
      for (const child of rec.data as unknown[]) {
        visitNode(child);
      }
    }
  };
  if (next.grid?.root !== undefined) {
    visitNode(next.grid.root);
  }

  if (!inserted) {
    return state;
  }

  next.panels = {
    ...(next.panels ?? {}),
    [target]: {
      id: target,
      contentComponent: target,
      title: PANEL_TITLES[target],
    },
  };
  return next;
}

/**
 * The full saved-layout migration applied at restore, in order: retired
 * panel-id rewrites (:func:`migrateLayoutPanels`) then new-panel insertion
 * (:func:`insertQueryPanel`).
 *
 * This is the single seam :func:`readSavedDockviewState` calls; the two
 * steps stay separately exported so each is testable on its own and a
 * future migration composes here rather than at the call site.
 */
/**
 * Insert the ``tracks`` panel into a saved layout that predates it, as a tab
 * in the group containing ``detections``, immediately after it (ADR-041 D-5
 * / REQ-UI-179 — the ADR-038 D15 precedent, anchored on Detections).
 *
 * Same contract as :func:`insertQueryPanel`: idempotent, anchor-only (a
 * layout with no Detections tab is left alone — the Tracks panel stays
 * reachable from the Add-panel menu), the operator's active tab is never
 * changed, the input is never mutated. Runs AFTER the id rewrites so a
 * legacy ``events`` tab has already become ``detections`` and anchors.
 */
export function insertTracksPanel(state: SerializedDockview): SerializedDockview {
  return insertPanelAfter(state, "tracks", "detections");
}

/** The shared insert-after-anchor step behind the two panel insertions. */
function insertPanelAfter(
  state: SerializedDockview,
  target: PanelId,
  anchor: PanelId,
): SerializedDockview {
  if (state.panels?.[target] !== undefined) {
    return state;
  }
  const next = JSON.parse(JSON.stringify(state)) as SerializedDockview;
  let inserted = false;
  const visitNode = (node: unknown): void => {
    if (inserted || node === null || typeof node !== "object") {
      return;
    }
    const rec = node as Record<string, unknown>;
    if (rec.type === "leaf" && rec.data !== null && typeof rec.data === "object") {
      const data = rec.data as Record<string, unknown>;
      if (!Array.isArray(data.views)) return;
      const views = data.views as unknown[];
      const anchorIndex = views.indexOf(anchor);
      if (anchorIndex === -1) return;
      views.splice(anchorIndex + 1, 0, target);
      inserted = true;
    } else if (rec.type === "branch" && Array.isArray(rec.data)) {
      for (const child of rec.data as unknown[]) {
        visitNode(child);
      }
    }
  };
  if (next.grid?.root !== undefined) {
    visitNode(next.grid.root);
  }
  if (!inserted) {
    return state;
  }
  next.panels = {
    ...(next.panels ?? {}),
    [target]: { id: target, contentComponent: target, title: PANEL_TITLES[target] },
  };
  return next;
}

export function migrateSavedLayout(state: SerializedDockview): SerializedDockview {
  return insertTracksPanel(insertQueryPanel(migrateLayoutPanels(state)));
}
